/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { getFocusStyle, getTheme, mergeStyles } from '@fluentui/style-utilities';
import { Property } from 'csstype';
import * as React from 'react';
import { getScopedTheme, getStylistV2 } from 'src/core/themes/luisStylist';
import { ViewOptions } from 'src/modules/labeling/types/ViewOptions';
import {
    bracketDataAttribute,
    tokenDataAttribute,
    tokenIndexDataAttribute,
    TOKEN_Z_INDEX
} from 'src/modules/labeling/utils/labelingConstants';
import { CalcMaxLevelReturn, calcTokenVerticalMargin } from 'src/modules/labeling/utils/layout';

export type TokenRendererProps = {
    /**
     * Children to be rendered.
     */
    children: React.ReactNode;

    /**
     * Indicates whether the token has a conflicting label and prediction or not.
     */
    hasConflict?: boolean;

    /**
     * Indicates whether the token is part of the selection or not.
     */
    selected?: boolean;

    /**
     * CSS style object to get applied to the root element.
     */
    style?: React.CSSProperties;

    /**
     * Indicates whether this token is the start of the selection
     */
    hasStartBracket?: boolean;

    /**
     * Indicates whether this token is the end of the selection
     */
    hasEndBracket?: boolean;

    /**
     * The maximum height level in the utterance.
     */
    maxLevels: CalcMaxLevelReturn;

    /**
     * Labeling view options.
     */
    viewOptions: ViewOptions;

    /**
     * Whether the utterance is RTL or not.
     */
    isRTL: boolean;

    /**
     * Data attribute that carries the token index.
     */
    [tokenIndexDataAttribute]: string;

    /**
     * Make visual tweaks to the labeler to make
     * it compatible with char tokenization.
     */
    useCharTokenization: boolean;
} & React.HTMLAttributes<HTMLElement>;

const focusStyles = mergeStyles(getFocusStyle(getTheme()));

const { getClassName } = getStylistV2('TokenRenderer');
const theme = getScopedTheme('TokenRenderer');

const BRACKET_TOP_MARGIN = -10;

const tokenTextClassName = getClassName('TokenText', {
    position: 'relative',
    userSelect: 'none'
});

const bracketClassName = getClassName('Bracket', {
    position: 'absolute',
    top: BRACKET_TOP_MARGIN,
    fontFamily: theme.bracketFontFamily,
    fontSize: theme.bracketFontSize,
    fontWeight: theme.bracketFontWeight as Property.FontWeight
});

/**
 * Represents the utterance token that gets rendered as part of the Renderer component.
 */
export const TokenRenderer = React.memo(
    React.forwardRef((props: TokenRendererProps, ref: React.MutableRefObject<HTMLSpanElement>) => {
        const {
            children,
            hasConflict = false,
            selected = false,
            style,
            hasEndBracket = false,
            hasStartBracket = false,
            maxLevels,
            viewOptions: { isBold, hideNames },
            isRTL,
            [tokenIndexDataAttribute]: tokenIndexDataAttr,
            useCharTokenization,
            ...rest
        } = props;

        const state = selected ? 'selected' : hasConflict ? 'conflict' : 'none';

        const parentDataAttrObj = { [tokenIndexDataAttribute]: `${tokenIndexDataAttr}` };
        const tokenDataAttrObj = { [tokenDataAttribute]: true };
        const bracketDataAttrObj = { [bracketDataAttribute]: true };
        const startBracket = (
            <span className={bracketClassName} {...bracketDataAttrObj} style={{ left: -2 }}>
                [
            </span>
        );
        const endBracket = (
            <span className={bracketClassName} {...bracketDataAttrObj} style={{ right: -2 }}>
                ]
            </span>
        );

        return (
            <span
                {...parentDataAttrObj}
                style={{
                    paddingTop: calcTokenVerticalMargin(maxLevels.upperMostLevel, hideNames),
                    paddingBottom: calcTokenVerticalMargin(Math.max(maxLevels.upperMostLevel, maxLevels.lowerMostLevel), hideNames),
                    whiteSpace: 'pre'
                }}>
                <span
                    ref={ref}
                    style={{
                        ...style,
                        padding: !useCharTokenization && '0px 5px',
                        zIndex: TOKEN_Z_INDEX,
                        fontWeight: (isBold ? theme.tokenBoldFont : theme.tokenRegularFont) as Property.FontWeight,
                        backgroundColor:
                            state === 'selected'
                                ? theme.selectedTokenBackground
                                : state === 'conflict'
                                ? theme.conflictTokenBackground
                                : 'transparent'
                    }}
                    className={`${focusStyles} ${tokenTextClassName}`}
                    {...tokenDataAttrObj}
                    {...rest}>
                    {hasStartBracket && (isRTL ? endBracket : startBracket)}
                    {children}
                    {hasEndBracket && (isRTL ? startBracket : endBracket)}
                </span>
            </span>
        );
    })
);

TokenRenderer.displayName = 'TokenRenderer';
